import React from 'react';
import { UploadIcon, ChevronRightIcon, KeyIcon, BuildingOfficeIcon, InformationCircleIcon, SpinnerIcon, CheckCircleIcon } from '../../../../components/Icons';
import { TARGET_FIELDS } from './OperationConstants';

// --- FILE STEP ---
export const FileUploadStep: React.FC<{ onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void }> = ({ onUpload }) => (
    <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-200 rounded-[2rem] bg-gray-50/50">
        <UploadIcon className="w-16 h-16 text-sky-400 mb-4" />
        <h4 className="text-lg font-black text-gray-800 uppercase mb-2">Sube tu archivo</h4>
        <p className="text-sm text-gray-500 mb-6 text-center max-w-xs">Aceptamos Excel (.xlsx, .xls) y CSV. Los encabezados deben estar en la primera fila.</p>
        <input type="file" id="mass-upload" accept=".xlsx, .xls, .csv" onChange={onUpload} className="hidden" />
        <label htmlFor="mass-upload" className="px-10 py-3 bg-gray-900 text-white rounded-2xl font-black uppercase text-xs tracking-widest cursor-pointer hover:bg-black transition-all">Seleccionar Archivo</label>
    </div>
);

// --- CONFIG STEP ---
export const ConfigStep: React.FC<{
    sheets: string[];
    selectedSheet: string;
    onSheetChange: (s: string) => void;
    selectedOwner: string;
    onOwnerChange: (o: string) => void;
    allowedOwners: string[];
    primaryKey: 'codigo' | 'codigo_brp';
    onPkTypeChange: (pk: 'codigo' | 'codigo_brp') => void;
    operationMode: 'update' | 'insert';
    onOperationModeChange: (mode: 'update' | 'insert') => void;
    onNext: () => void;
}> = ({ sheets, selectedSheet, onSheetChange, selectedOwner, onOwnerChange, allowedOwners, primaryKey, onPkTypeChange, operationMode, onOperationModeChange, onNext }) => (
    <div className="space-y-6 animate-fade-in">
        <div className="p-5 bg-sky-50 rounded-3xl border border-sky-100">
            <label className="block text-[10px] font-black text-sky-600 uppercase tracking-widest mb-3 text-center">1. Hoja de Trabajo</label>
            <select value={selectedSheet} onChange={(e) => onSheetChange(e.target.value)} className="w-full border-2 border-sky-200 rounded-xl p-3 text-sm font-bold text-sky-900 outline-none focus:ring-2 focus:ring-sky-500">
                {sheets.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
        </div>

        <div className="p-5 bg-gray-50 rounded-3xl border border-gray-100">
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 text-center">2. Modo de Operación</label>
            <div className="grid grid-cols-2 gap-4">
                <button 
                    onClick={() => onOperationModeChange('update')} 
                    className={`p-4 rounded-2xl border-2 transition-all text-center ${operationMode === 'update' ? 'bg-white border-sky-500 shadow-lg' : 'bg-transparent border-gray-200 opacity-60'}`}
                >
                    <span className="block text-xs font-black uppercase">Actualizar</span>
                </button>
                <button 
                    onClick={() => onOperationModeChange('insert')} 
                    className={`p-4 rounded-2xl border-2 transition-all text-center ${operationMode === 'insert' ? 'bg-white border-sky-500 shadow-lg' : 'bg-transparent border-gray-200 opacity-60'}`}
                >
                    <span className="block text-xs font-black uppercase">Insertar Nuevos</span>
                </button>
            </div>
        </div>

        <div className="p-5 bg-gray-50 rounded-3xl border border-gray-100">
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 text-center">3. Selecciona el Owner (Propietario)</label>
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-sky-600">
                    <BuildingOfficeIcon className="w-5 h-5" />
                </div>
                <select value={selectedOwner} onChange={(e) => onOwnerChange(e.target.value)} className="w-full pl-10 border-2 border-gray-200 rounded-xl p-3 text-sm font-bold text-gray-900 outline-none focus:border-sky-500 transition-all">
                    <option value="">-- Seleccionar Propietario --</option>
                    {allowedOwners.map(o => <option key={o} value={o}>{o.replace(/-/g, '_')}</option>)}
                </select>
            </div>
        </div>

        {operationMode === 'update' && (
            <div className="p-5 bg-gray-50 rounded-3xl border border-gray-100">
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 text-center">4. Llave de Búsqueda</label>
                <div className="grid grid-cols-2 gap-4">
                    <button onClick={() => onPkTypeChange('codigo_brp')} className={`p-4 rounded-2xl border-2 transition-all text-left ${primaryKey === 'codigo_brp' ? 'bg-white border-sky-500 shadow-lg' : 'bg-transparent border-gray-200 opacity-60'}`}>
                        <span className="block text-xs font-black uppercase mb-1">Cód. BRP</span>
                    </button>
                    <button onClick={() => onPkTypeChange('codigo')} className={`p-4 rounded-2xl border-2 transition-all text-left ${primaryKey === 'codigo' ? 'bg-white border-sky-500 shadow-lg' : 'bg-transparent border-gray-200 opacity-60'}`}>
                        <span className="block text-xs font-black uppercase mb-1">ID Interno</span>
                    </button>
                </div>
            </div>
        )}

        <button onClick={onNext} disabled={!selectedOwner} className="w-full py-4 bg-sky-600 text-white rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-xl hover:bg-sky-700 flex items-center justify-center gap-2 disabled:opacity-50">Configurar Mapeo <ChevronRightIcon className="w-5 h-5"/></button>
    </div>
);

// --- MAPPING STEP ---
export const MappingStep: React.FC<{
    pkMapping: string;
    onPkMappingChange: (v: string) => void;
    headers: string[];
    primaryKey: string;
    selectedOwner: string;
    mapping: Record<string, string>;
    onMappingChange: (h: string, f: string) => void;
    operationMode: 'update' | 'insert';
    onBack: () => void;
    onExecute: () => void;
}> = ({ pkMapping, onPkMappingChange, headers, primaryKey, selectedOwner, mapping, onMappingChange, operationMode, onBack, onExecute }) => (
    <div className="space-y-6 animate-fade-in">
        {operationMode === 'update' ? (
            <div className="p-6 bg-sky-900 text-white rounded-[2rem] shadow-xl border border-sky-700">
                <div className="flex items-center gap-3 mb-4"><KeyIcon className="w-6 h-6 text-sky-300" /><h4 className="text-sm font-black uppercase tracking-widest">Identificador para {selectedOwner.replace(/-/g, '_')}</h4></div>
                <select value={pkMapping} onChange={(e) => onPkMappingChange(e.target.value)} className="w-full bg-white/10 border-2 border-white/20 rounded-xl p-3 text-sm font-bold text-white outline-none focus:border-sky-400">
                    <option value="" className="text-gray-900">-- Selecciona la columna --</option>
                    {headers.map(h => <option key={h} value={h} className="text-gray-900">{h}</option>)}
                </select>
            </div>
        ) : (
            <div className="p-4 bg-sky-50 rounded-2xl border border-sky-100 flex items-start gap-3">
                <InformationCircleIcon className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-sky-700 font-medium">En el modo <b>Insertar</b>, se crearán nuevos registros con el Propietario seleccionado y visibilidad configurada por defecto.</p>
            </div>
        )}
        
        <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar border-y border-gray-100">
            <table className="w-full">
                <tbody className="divide-y divide-gray-50">
                    {headers.map(h => (
                        <tr key={h} className={operationMode === 'update' && h === pkMapping ? 'bg-sky-50/50 opacity-50' : ''}>
                            <td className="py-3 text-sm font-bold text-gray-700">{h} {operationMode === 'update' && h === pkMapping && <span className="ml-2 text-[9px] bg-sky-600 text-white px-1.5 py-0.5 rounded-full">LLAVE</span>}</td>
                            <td className="py-3">
                                <select 
                                    value={mapping[h] || ""} 
                                    onChange={(e) => onMappingChange(h, e.target.value)} 
                                    disabled={operationMode === 'update' && h === pkMapping} 
                                    className="w-full text-xs font-bold border-2 border-gray-100 rounded-lg p-1.5 bg-gray-50/50"
                                >
                                    <option value="">-- Ignorar --</option>
                                    {TARGET_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                                </select>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        <div className="flex gap-3 pt-4">
            <button onClick={onBack} className="flex-1 py-4 border-2 border-gray-100 rounded-2xl font-black uppercase text-xs text-gray-400">Atrás</button>
            <button onClick={onExecute} disabled={operationMode === 'update' && !pkMapping} className="flex-[2] py-4 bg-gray-900 text-white rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-xl disabled:opacity-50">
                {operationMode === 'update' ? 'Iniciar Actualización' : 'Iniciar Inserción'}
            </button>
        </div>
    </div>
);

// --- REPORT STEP ---
export const ReportStep: React.FC<{ results: any; onComplete: () => void; operationMode: 'update' | 'insert' }> = ({ results, onComplete, operationMode }) => (
    <div className="space-y-6 animate-fade-in">
        <div className="text-center"><CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" /><h3 className="text-2xl font-black text-gray-900 uppercase tracking-tight">Proceso Finalizado</h3></div>
        <div className="grid grid-cols-2 gap-4">
            <div className="p-5 bg-green-50 rounded-3xl border border-green-100 text-center">
                <span className="block text-3xl font-black text-green-600">{results.success}</span>
                <span className="text-[10px] font-black text-green-800 uppercase">{operationMode === 'update' ? 'Actualizados' : 'Creados'}</span>
            </div>
            <div className="p-5 bg-red-50 rounded-3xl border border-red-100 text-center">
                <span className="block text-3xl font-black text-red-600">{results.failed}</span>
                <span className="text-[10px] font-black text-red-800 uppercase">Fallidos</span>
            </div>
        </div>

        {results.errors?.length > 0 && (
            <div className="p-5 bg-gray-50 rounded-3xl border border-gray-200">
                <h5 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Registros no encontrados o erróneos:</h5>
                <div className="max-h-32 overflow-y-auto custom-scrollbar flex flex-wrap gap-2">
                    {results.errors.map((err: string, i: number) => (
                        <span key={i} className="px-2 py-1 bg-white border border-gray-200 rounded-lg text-[10px] font-bold text-red-600 font-mono">{err}</span>
                    ))}
                </div>
            </div>
        )}

        <button onClick={onComplete} className="w-full py-4 bg-[#0073b7] text-white rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-xl hover:opacity-90">Cerrar y Refrescar</button>
    </div>
);